/**
 * DuplicateAuditDialog.js
 *
 * Modal shown when AppAdmin clicks the "Duplicate" icon on an audit row.
 *
 * Presents THREE big tiles instead of checkboxes — user clicks a tile and
 * the action happens immediately (no separate "Duplicate" submit button).
 *
 *   Tile 1 — Copy Manufacturing Facility
 *              Duplicates Form A only. New audit created.
 *              → navigates to /form-a/{newAuditId}
 *
 *   Tile 2 — Copy Complete Report
 *              Duplicates all 6 forms. New audit created.
 *              → navigates to /form-a/{newAuditId}
 *
 *   Tile 3 — Start Fresh
 *              No copy at all. Nothing written yet.
 *              → navigates to /form-a/new (creation-mode Form A)
 *
 * In every case the user lands on Form A. For the two copy modes the
 * reporting period is cleared, so they need to review dates anyway.
 *
 * Props:
 *   open           : bool
 *   sourceAudit    : { id, name, description }
 *   onClose        : () => void
 *   onDuplicated   : (newAuditId, newName) => void
 *                    Fired ONLY for the two Copy modes (not Start Fresh),
 *                    so the parent can refresh its list if it wants.
 *                    Since we navigate away anyway, parent typically won't
 *                    need to do anything except close the dialog.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Alert, Box, Stack, Typography, alpha,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useAuth } from '../auth/AuthContext';
import { useFormContext } from '../context/FormContext';
import { duplicateAudit, DUPLICATE_MODES } from '../utils/duplicateAudit';

// Palette (matches AuditList redesign)
const TOKENS = {
  gradient: 'linear-gradient(135deg, #7C6EF2 0%, #4A90E2 100%)',
  ink: '#101B3D',
};

// ─── Sub-component: a single clickable tile ─────────────────────────────────
const OptionTile = ({
  IconCmp, iconColor, iconBg, title, description, onClick, loading, disabled,
}) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      px: 2,
      py: 2,
      borderRadius: 2.5,
      border: `1px solid ${alpha(TOKENS.ink, 0.1)}`,
      bgcolor: '#fff',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled && !loading ? 0.55 : 1,
      transition: 'all 0.15s ease',
      '&:hover': disabled ? {} : {
        borderColor: alpha('#7C6EF2', 0.5),
        bgcolor: alpha('#7C6EF2', 0.03),
        transform: 'translateY(-1px)',
        boxShadow: `0 8px 20px ${alpha(TOKENS.ink, 0.06)}`,
      },
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 44,
        height: 44,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: iconBg,
      }}
    >
      {loading
        ? <CircularProgress size={20} sx={{ color: iconColor }} />
        : <IconCmp sx={{ fontSize: 24, color: iconColor }} />
      }
    </Box>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: TOKENS.ink }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: alpha(TOKENS.ink, 0.6), mt: 0.25, lineHeight: 1.45 }}>
        {description}
      </Typography>
    </Box>

    <ArrowForwardRoundedIcon
      sx={{ fontSize: 20, color: alpha(TOKENS.ink, 0.3), flexShrink: 0 }}
    />
  </Box>
);

// ─── Main dialog ────────────────────────────────────────────────────────────
const DuplicateAuditDialog = ({ open, sourceAudit, onClose, onDuplicated }) => {
  const navigate = useNavigate();
  const { currentUser, tenantId, tenantName } = useAuth();
  const { clearDraft } = useFormContext();

  //  Track which tile is currently running (so we can show a spinner on
  //  just that tile and disable the others).
  const [busyMode, setBusyMode] = useState(null);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (busyMode) return; // no close mid-write
    setError('');
    onClose();
  };

  //  Shared handler for both copy modes.
  const runCopy = async (mode) => {
    if (!sourceAudit?.id) {
      setError('Source audit is missing.');
      return;
    }
    if (!tenantId) {
      setError('No tenant assigned to your account.');
      return;
    }

    setBusyMode(mode);
    setError('');
    try {
      clearDraft();
      const { newAuditId, newName } = await duplicateAudit({
        sourceAuditId: sourceAudit.id,
        mode,
        user: {
          email: currentUser?.email || '',
          tenantId,
          tenantName: tenantName || '',
        },
      });
      //  Close dialog + notify parent, then navigate to Form A of new audit
      if (typeof onDuplicated === 'function') {
        onDuplicated(newAuditId, newName);
      } else {
        onClose();
      }
      navigate(`/form-a/${newAuditId}`);
    } catch (err) {
      console.error('Duplicate failed:', err);
      setError('Could not duplicate. ' + (err?.message || 'Please try again.'));
      setBusyMode(null);
    }
  };

  //  Start Fresh → no Firestore write. Just navigate to creation-mode Form A.
  const runStartFresh = () => {
    onClose();
    navigate('/form-a/new');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContentCopyRoundedIcon sx={{ color: '#7C6EF2' }} />
        Duplicate Audit
      </DialogTitle>

      <DialogContent>
        {sourceAudit?.name && (
          <Typography sx={{ fontSize: 13, color: alpha(TOKENS.ink, 0.6), mb: 1 }}>
            Source: <strong style={{ color: TOKENS.ink }}>{sourceAudit.name}</strong>
          </Typography>
        )}

        <Typography sx={{ fontSize: 13, color: alpha(TOKENS.ink, 0.6), mb: 2 }}>
          How would you like to duplicate this?
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Stack spacing={1.5}>
          <OptionTile
            IconCmp={DescriptionRoundedIcon}
            iconColor="#4A90E2"
            iconBg={alpha('#4A90E2', 0.12)}
            title="Copy Manufacturing Facility"
            description="Carries over manufacturing facility, processes, and goods (Form A). Reporting period will be cleared — you'll set new dates."
            onClick={() => runCopy(DUPLICATE_MODES.INSTALLATION_ONLY)}
            loading={busyMode === DUPLICATE_MODES.INSTALLATION_ONLY}
            disabled={!!busyMode}
          />

          <OptionTile
            IconCmp={LayersRoundedIcon}
            iconColor="#7C6EF2"
            iconBg={alpha('#7C6EF2', 0.12)}
            title="Copy Complete Report"
            description="Carries over all data (Forms A–F). Reporting period will be cleared — you'll set new dates."
            onClick={() => runCopy(DUPLICATE_MODES.COMPLETE)}
            loading={busyMode === DUPLICATE_MODES.COMPLETE}
            disabled={!!busyMode}
          />

          <OptionTile
            IconCmp={AutoAwesomeRoundedIcon}
            iconColor="#F5A623"
            iconBg={alpha('#F5A623', 0.12)}
            title="Duplicate with Defaults"
            description="Keeps only default emission values and clears the rest of the audit data."
            onClick={() => runCopy(DUPLICATE_MODES.DEFAULTS_ONLY)}
            loading={busyMode === DUPLICATE_MODES.DEFAULTS_ONLY}
            disabled={!!busyMode}
          />

          <OptionTile
            IconCmp={AutoAwesomeRoundedIcon}
            iconColor="#22B573"
            iconBg={alpha('#22B573', 0.12)}
            title="Start Fresh"
            description="Blank new audit — nothing carried over from the source."
            onClick={runStartFresh}
            loading={false}
            disabled={!!busyMode}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          disabled={!!busyMode}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateAuditDialog;